import type { Result } from "neverthrow";
import { err, ok, Result as Results } from "neverthrow";
import type { SnakeCase } from "type-fest";

import {
  MEANING_KEYWORD,
  type LanguageMap,
} from "../../assessments/authoring.js";
import {
  notePlacements,
  type TranslatorUnbranded,
} from "../../assessments/presentation.js";
import {
  closedValuesOf,
  commentKeysIn,
  commentTargetOf,
  resolveResponse,
  responseSetOf,
  type PublishedInstrumentSchema,
} from "../../assessments/publishedSchema.js";
import { pointerFor } from "../../assessments/validate.js";
import type { Check } from "./checks.js";
import { refusal, type Refusal } from "./refusal.js";
import type { Asked, CommentRequired, Rule } from "./rules.js";

/** A control `controlOf` may give an item in place of its plain one. */
export type PreferableControl = "date";

/** How strongly a banner asks to be noticed: a hint a renderer may honour or ignore. */
export type BannerSeverity = "info" | "warning" | "error";

/** The placements beside a comment box, which only an item taking a comment has. */
export const commentPlacements = ["before_comment", "after_comment"] as const;
commentPlacements satisfies readonly SnakeCase<
  (typeof commentPlacements)[number]
>[];

/**
 * Where a banner sits beside an item, in the order a respondent reads it: the
 * core's note placements, then beside the item's comment box. A hint as
 * severity is, in that it says where rather than how to draw it; the evidence
 * attests a banner was presented there, so a renderer not honouring placements
 * should leave it out of the evidence rather than attest it.
 */
export const bannerPlacements = [...notePlacements, ...commentPlacements];

export type BannerPlacement = (typeof bannerPlacements)[number];

export type CommentPlacement = (typeof commentPlacements)[number];

/** Whether a banner sits beside the comment box rather than its item's question or answers. */
export const isCommentPlacement = (
  placement: BannerPlacement,
): placement is CommentPlacement =>
  commentPlacements.some((candidate) => candidate === placement);

/** A value a closed choice may offer. */
export type ChoiceValue = string | number;

/**
 * One text a form shows, and where it came from, as `presentationOf` records
 * it: the instrument's meaning in the form's language, a decorator's own
 * words in it, a machine translation of another text, or the bare key or
 * value where there is none of those, which is in no language.
 */
export type FormText =
  | {
      readonly text: string;
      readonly language: string;
      readonly origin:
        | { readonly kind: "canonical" | "collector" }
        | {
            readonly kind: "machine-translated";
            readonly from: FormText;
            readonly by: TranslatorUnbranded;
          };
    }
  | { readonly text: string; readonly origin: { readonly kind: "identifier" } };

/** What a form shows for something: a title, and any description. */
export type Shown = {
  readonly title: FormText;
  readonly description?: FormText;
};

/** A collector's text shown beside an item, framing it. */
export type Banner = {
  readonly placement: BannerPlacement;
  readonly severity: BannerSeverity;
  readonly text: FormText;
  /** Shown only while this holds, as `withBannersShown` decides; always shown without it. */
  readonly shownWhen?: Rule;
};

/** The plainest control a JSON type allows. */
export type PlainControl =
  | {
      readonly kind: "choice";
      readonly options: readonly (Shown & { readonly value: ChoiceValue })[];
    }
  | { readonly kind: "text" };

/** How a form collects one response: its plain control, or a richer one a decorator gives it. */
export type FormControl = PlainControl | { readonly kind: "date" };

/**
 * Whether a document may leave something out, and if not, who insists.
 *
 * For an item, `by_instrument` and `by_form` alike hold only where its group
 * is present at all: an item required inside an optional group is left out
 * with it. `by_form` is a
 * decorator's narrowing of something the instrument lets a document omit,
 * and holds only while the item is asked.
 */
export type Requirement = "no" | "by_instrument" | "by_form";

/**
 * One thing a form presents, in the order it presents them.
 *
 * An element is grouped by concern: `presentation` is what the respondent is
 * shown, `asking` whether the element is asked and what must then be given,
 * and `checks` how an answer is judged.
 */
export type FormElement =
  | {
      readonly kind: "group";
      readonly at: { readonly group: string };
      readonly presentation: Shown;
      readonly asking: {
        readonly when: Asked;
        readonly required: Exclude<Requirement, "by_form">;
      };
    }
  | {
      readonly kind: "item";
      readonly at: { readonly group: string; readonly item: string };
      /** The id of the shared response set the item draws on, where it draws on one. */
      readonly responseSet?: string;
      /**
       * Where the item's comment is answered, where the instrument declares one:
       * a comment is part of its item's question rather than one of its own.
       */
      readonly commentAt?: { readonly group: string; readonly item: string };
      readonly presentation: Shown & {
        readonly control: FormControl;
        readonly acceptsComment: boolean;
        /** The collector's texts shown beside the item, framing it, in the order they are read. */
        readonly banners: readonly Banner[];
      };
      readonly asking: {
        readonly when: Asked;
        readonly required: Requirement;
        readonly commentRequired: CommentRequired;
      };
      readonly checks: readonly Check<unknown>[];
    };

/**
 * A stored schema's entries in code-unit order of key.
 *
 * Sorted because a stored schema carries no order of its own — `JSONB` keeps
 * none, and the digest's canonical spelling sorts keys anyway — so this is the
 * order where nobody has chosen one. Code-unit order rather than
 * `localeCompare`, so it cannot vary with a runtime's locale data.
 */
const byKey = <Value>(
  record: Readonly<Record<string, Value>>,
): [string, Value][] =>
  Object.entries(record).sort(([left], [right]) => (left < right ? -1 : 1));

/**
 * A meaning in one language, or `fallback` for its title where there is none:
 * a version need not declare meanings, so a missing one is not a failure.
 */
const shownIn = (
  meaning: { title: LanguageMap; description?: LanguageMap } | undefined,
  language: string,
  fallback: string,
): Shown => {
  const canonical = (text: string): FormText => ({
    text,
    language,
    origin: { kind: "canonical" },
  });
  const title = meaning?.title[language];
  const description = meaning?.description?.[language];

  return {
    title:
      title === undefined
        ? { text: fallback, origin: { kind: "identifier" } }
        : canonical(title),
    ...(description === undefined
      ? {}
      : { description: canonical(description) }),
  };
};

const isChoiceValue = (value: unknown): value is ChoiceValue =>
  typeof value === "string" || typeof value === "number";

/** Why the item at a JSON pointer has no plain control. */
const noPlainControl = (at: string, reason: string): Refusal =>
  refusal({
    message: "An item has no plain control",
    issues: [`${at}: ${reason}`],
  });

/** The plainest control a response's JSON type allows, or why it has none. */
const plainControlOf = ({
  at,
  response,
  schema,
  answerOptions,
  language,
}: {
  at: string;
  response: Readonly<Record<string, unknown>>;
  schema: PublishedInstrumentSchema;
  /** What the item says each of its values means, where it says. */
  answerOptions:
    | readonly {
        value: ChoiceValue;
        title: LanguageMap;
        description?: LanguageMap;
      }[]
    | undefined;
  language: string;
}): Result<PlainControl, Refusal> => {
  const defs = schema.$defs ?? {};
  const resolved = resolveResponse(response, defs);

  if (resolved === undefined) {
    return err(
      noPlainControl(
        at,
        "its response refers to a definition the schema lacks",
      ),
    );
  }

  // Publishing holds a meaning's answer options to this same set, so every
  // value a meaning describes is one the form offers.
  const values = closedValuesOf(response, defs);

  if (values !== undefined) {
    return values.every(isChoiceValue)
      ? ok({
          kind: "choice",
          options: values.map((value) => ({
            value,
            ...shownIn(
              answerOptions?.find(
                (answerOption) => answerOption.value === value,
              ),
              language,
              String(value),
            ),
          })),
        })
      : err(
          noPlainControl(
            at,
            "it offers a choice that is not all strings and numbers",
          ),
        );
  }

  return resolved["type"] === "string"
    ? ok({ kind: "text" })
    : err(noPlainControl(at, "its response has no plain control"));
};

/**
 * The form for an instrument version, from its stored schema alone, meanings
 * included, so it depends on no collector code and nothing that can change
 * after publication.
 *
 * A comment is not an element: it is asked as part of the item it is about,
 * whose element says where it is answered.
 *
 * Refused where an item has no plain control, naming every such item by JSON
 * pointer.
 */
export const plainFormOf = ({
  schema,
  language,
}: {
  schema: PublishedInstrumentSchema;
  language: string;
}): Result<FormElement[], Refusal> => {
  const required = (
    list: readonly string[] | undefined,
    key: string,
  ): Exclude<Requirement, "by_form"> =>
    (list ?? []).includes(key) ? "by_instrument" : "no";

  const elements = byKey(schema.properties).flatMap(([group, groupSchema]) => {
    const comments = commentKeysIn(groupSchema);
    const answers = byKey(groupSchema.properties).filter(
      ([, itemSchema]) => commentTargetOf(itemSchema) === undefined,
    );

    return [
      ok<FormElement, Refusal>({
        kind: "group",
        at: { group },
        presentation: shownIn(groupSchema[MEANING_KEYWORD], language, group),
        asking: { when: "always", required: required(schema.required, group) },
      }),
      ...answers.map(([item, itemSchema]) => {
        const meaning = itemSchema[MEANING_KEYWORD];
        const responseSet = responseSetOf(itemSchema.properties.response);
        const comment = comments.get(item);

        return plainControlOf({
          at: pointerFor([group, item]),
          response: itemSchema.properties.response,
          schema,
          answerOptions: meaning?.answers,
          language,
        }).map((control): FormElement => ({
          kind: "item",
          at: { group, item },
          ...(responseSet === undefined ? {} : { responseSet }),
          ...(comment === undefined
            ? {}
            : { commentAt: { group, item: comment } }),
          presentation: {
            ...shownIn(meaning, language, item),
            control,
            acceptsComment: comment !== undefined,
            banners: [],
          },
          asking: {
            when: "always",
            required: required(groupSchema.required, item),
            commentRequired: "never",
          },
          checks: [],
        }));
      }),
    ];
  });

  return Results.combineWithAllErrors(elements).mapErr((errors) =>
    refusal({
      message: "The instrument has no plain form",
      issues: errors.flatMap((error) => error.issues),
      suppressed: errors,
    }),
  );
};
